// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Skill Tree SVG Visualization
// Renders the growth roadmap as a vertical branching tree with SVG connectors
// between parent and child milestones. Foundation (Lv.1) at bottom,
// Ultimate Goal (Lv.5) at top. Connector color reflects parent completion.
// ══════════════════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { Flame, Lock, Clock, CheckCircle2 } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';

const NODE_WIDTH = 180;
const NODE_HEIGHT = 80;
const LEVEL_GAP = 110;   // vertical distance between levels
const NODE_GAP = 28;     // horizontal distance between siblings

export function SkillTreeSVG({ milestones = [], onNodeClick }) {
  const { theme } = useTheme();
  const { lang, isRTL } = useLang();

  // ─── Layout: compute (x, y) for each milestone ───────────────────────
  const layout = useMemo(() => {
    if (!milestones.length) return { nodes: [], edges: [], width: 0, height: 0 };

    // Group by level
    const byLevel = {};
    for (const m of milestones) {
      if (!byLevel[m.level]) byLevel[m.level] = [];
      byLevel[m.level].push(m);
    }
    // Sort each level by display_order then position_x
    Object.values(byLevel).forEach(arr =>
      arr.sort((a, b) =>
        (a.display_order || 0) - (b.display_order || 0) ||
        (a.position_x || 0) - (b.position_x || 0)
      )
    );

    // Find widest level — determines canvas width
    const levels = Object.keys(byLevel).map(Number).sort((a, b) => a - b);
    const widestLevel = Math.max(...levels.map(lvl => byLevel[lvl].length));
    const canvasWidth = Math.max(500, widestLevel * (NODE_WIDTH + NODE_GAP) + NODE_GAP);

    // Assign positions. Level 1 at bottom, highest level at top.
    const maxLevel = Math.max(...levels);
    const nodes = [];
    for (const lvl of levels) {
      const row = byLevel[lvl];
      const totalRowWidth = row.length * NODE_WIDTH + (row.length - 1) * NODE_GAP;
      const startX = (canvasWidth - totalRowWidth) / 2;
      const y = (maxLevel - lvl) * LEVEL_GAP + 20;
      row.forEach((m, idx) => {
        nodes.push({
          ...m,
          _x: startX + idx * (NODE_WIDTH + NODE_GAP),
          _y: y,
          _cx: startX + idx * (NODE_WIDTH + NODE_GAP) + NODE_WIDTH / 2,
          _cy: y + NODE_HEIGHT / 2
        });
      });
    }

    // Build edges: parent_milestone_id → child
    const edges = [];
    const nodeById = new Map(nodes.map(n => [n.id, n]));
    for (const n of nodes) {
      if (!n.parent_milestone_id) continue;
      const parent = nodeById.get(n.parent_milestone_id);
      if (!parent) continue;
      edges.push({
        from: parent,
        to: n,
        parentCompleted: parent.is_completed
      });
    }

    const canvasHeight = (maxLevel - Math.min(...levels)) * LEVEL_GAP + NODE_HEIGHT + 40;
    return { nodes, edges, width: canvasWidth, height: canvasHeight };
  }, [milestones]);

  if (!milestones.length) return null;

  const { nodes, edges, width, height } = layout;

  // ─── Color helpers per state ─────────────────────────────────────────
  const getNodeColors = (m) => {
    const state = m.gate?.state || 'ready';
    if (state === 'completed') return {
      fill: theme.success,
      stroke: theme.success,
      text: '#fff',
      icon: <CheckCircle2 size={14} color="#fff" />
    };
    if (state === 'ready') return {
      fill: theme.primaryDim,
      stroke: theme.primary,
      text: theme.text,
      icon: <Flame size={14} color={theme.primary} />
    };
    if (state === 'in_progress') return {
      fill: theme.warningDim,
      stroke: theme.warning,
      text: theme.text,
      icon: <Clock size={14} color={theme.warning} />
    };
    // locked
    return {
      fill: theme.bgInput,
      stroke: theme.textTertiary,
      text: theme.textSecondary,
      icon: <Lock size={14} color={theme.textTertiary} />
    };
  };

  const getEdgeColor = (edge) => {
    if (edge.parentCompleted) return theme.success;
    return theme.border;
  };

  return (
    <div style={{
      overflowX: 'auto',
      overflowY: 'hidden',
      padding: '10px 0',
      direction: 'ltr' // force LTR for SVG internal coords
    }}>
      <svg
        width={width}
        height={height}
        style={{
          display: 'block',
          margin: '0 auto'
        }}
      >
        <defs>
          <marker
            id="arrow-complete"
            viewBox="0 0 8 8"
            refX="6"
            refY="4"
            markerWidth="5"
            markerHeight="5"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill={theme.success} />
          </marker>
          <marker
            id="arrow-pending"
            viewBox="0 0 8 8"
            refX="6"
            refY="4"
            markerWidth="5"
            markerHeight="5"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill={theme.border} />
          </marker>
        </defs>

        {/* Edges (connectors) */}
        {edges.map((edge, i) => {
          const x1 = edge.from._cx;
          const y1 = edge.from._y; // top of parent node
          const x2 = edge.to._cx;
          const y2 = edge.to._y + NODE_HEIGHT; // bottom of child node
          // Bezier curve going upward
          const midY = (y1 + y2) / 2;
          const color = getEdgeColor(edge);
          return (
            <path
              key={i}
              d={`M ${x2} ${y2} C ${x2} ${midY}, ${x1} ${midY}, ${x1} ${y1}`}
              stroke={color}
              strokeWidth={edge.parentCompleted ? 2 : 1.5}
              strokeDasharray={edge.parentCompleted ? '0' : '4 3'}
              fill="none"
              markerEnd={edge.parentCompleted ? 'url(#arrow-complete)' : 'url(#arrow-pending)'}
              opacity={edge.parentCompleted ? 0.9 : 0.5}
            />
          );
        })}

        {/* Nodes */}
        {nodes.map(m => {
          const colors = getNodeColors(m);
          const title = lang === 'ar' ? m.title_ar : m.title_en;
          const truncated = title.length > 26 ? title.slice(0, 24) + '…' : title;
          return (
            <g
              key={m.id}
              transform={`translate(${m._x}, ${m._y})`}
              style={{ cursor: onNodeClick ? 'pointer' : 'default' }}
              onClick={() => onNodeClick?.(m)}
            >
              {/* Node background */}
              <rect
                width={NODE_WIDTH}
                height={NODE_HEIGHT}
                rx={10}
                fill={colors.fill}
                stroke={colors.stroke}
                strokeWidth={m.gate?.state === 'ready' ? 2 : 1.5}
              />

              {/* Level badge */}
              <rect
                x={10}
                y={10}
                width={34}
                height={18}
                rx={4}
                fill={colors.stroke}
                opacity={0.85}
              />
              <text
                x={27}
                y={22}
                textAnchor="middle"
                fontSize="10"
                fontWeight="700"
                fill="#fff"
              >
                Lv.{m.level}
              </text>

              {/* Title */}
              <text
                x={NODE_WIDTH / 2}
                y={48}
                textAnchor="middle"
                fontSize="12"
                fontWeight="600"
                fill={colors.text}
              >
                {truncated}
              </text>

              {/* Streak indicator */}
              {!m.is_completed && parseInt(m.consecutive_months_required || 0) > 0 && (
                <text
                  x={NODE_WIDTH / 2}
                  y={66}
                  textAnchor="middle"
                  fontSize="10"
                  fill={colors.text}
                  opacity={0.7}
                >
                  {m.consecutive_months_achieved || 0} / {m.consecutive_months_required} {lang === 'ar' ? 'شهر' : 'mo'}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
